import { useState, useEffect, useCallback } from 'react'
import {
  getTutorial,
  setTutorial,
} from '@sportninja/common/utils/TutorialHelper'
import { tutorialKeys } from '@sportninja/common/constants/userGuiding'

export type CompetitionSetupStatus =
  | 'not_started'
  | 'skipped'
  | 'in_progress'
  | 'completed'

interface UseCompetitionSetupTutorialProps {
  competitionId: string
  isLoggedIn: boolean
  isAdmin: boolean
  userEmail?: string
  isFromOnboarding?: boolean
}

interface UseCompetitionSetupTutorialReturn {
  shouldShowModal: boolean
  tutorialStatus: CompetitionSetupStatus | null
  isLoading: boolean
  error: string | null
  updateTutorialStatus: (status: CompetitionSetupStatus) => Promise<void>
  openModal: () => void
  closeModal: () => void
  isModalOpen: boolean
}

/**
 * Custom hook to manage the competition setup wizard tutorial state
 * Tracks tutorial completion per competition ID
 */
export const useCompetitionSetupTutorial = ({
  competitionId,
  isLoggedIn,
  isAdmin,
  userEmail,
  isFromOnboarding = false,
}: UseCompetitionSetupTutorialProps): UseCompetitionSetupTutorialReturn => {
  console.log(
    '🔄 useCompetitionSetupTutorial: Initializing hook for competition:',
    competitionId
  )

  const [tutorialStatus, setTutorialStatus] =
    useState<CompetitionSetupStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDebugMode, setIsDebugMode] = useState(false)

  // Check if conditions are met to show tutorial
  const shouldRun = competitionId && isLoggedIn && isAdmin

  // Check if user has @sportninja.com email for debug mode
  const isDebugUser = userEmail?.endsWith('@sportninja.com')

  /**
   * Generate a unique tutorial key for this competition
   */
  const getTutorialKey = useCallback(() => {
    return `${tutorialKeys.COMPETITION_SETUP_WIZARD_STATUS}_${competitionId}`
  }, [competitionId])

  /**
   * Fetch tutorial status from API
   */
  const fetchTutorialStatus = useCallback(async () => {
    console.log(
      '🔄 useCompetitionSetupTutorial: Fetching tutorial status for competition:',
      competitionId
    )
    try {
      setIsLoading(true)
      const tutorialKey = getTutorialKey()
      const result = await getTutorial(tutorialKey)

      if (result?.tutorialStep?.value) {
        const status = result.tutorialStep.value as CompetitionSetupStatus
        console.log('✅ useCompetitionSetupTutorial: Status fetched:', status)
        setTutorialStatus(status)
      } else {
        console.log(
          '⚠️ useCompetitionSetupTutorial: No status found, setting to not_started'
        )
        setTutorialStatus('not_started')
      }
    } catch (err) {
      console.error(
        '🚨 useCompetitionSetupTutorial: Error fetching status:',
        err
      )
      setError('Failed to fetch tutorial status')
      setTutorialStatus('not_started')
    } finally {
      setIsLoading(false)
    }
  }, [competitionId, getTutorialKey])

  /**
   * Update tutorial status in API
   */
  const updateTutorialStatus = useCallback(
    async (status: CompetitionSetupStatus) => {
      console.log('🔄 useCompetitionSetupTutorial: Updating status to:', status)
      try {
        const tutorialKey = getTutorialKey()
        await setTutorial(tutorialKey, status)
        setTutorialStatus(status)
        console.log(
          '✅ useCompetitionSetupTutorial: Status updated successfully'
        )

        // Close modal if completed or skipped
        if (status === 'completed' || status === 'skipped') {
          setIsModalOpen(false)
        }
      } catch (err) {
        console.error(
          '🚨 useCompetitionSetupTutorial: Error updating status:',
          err
        )
        setError('Failed to update tutorial status')
        throw err
      }
    },
    [getTutorialKey]
  )

  /**
   * Open the modal manually
   */
  const openModal = useCallback(() => {
    console.log('🔄 useCompetitionSetupTutorial: Opening modal')
    setIsModalOpen(true)
  }, [])

  /**
   * Close the modal
   */
  const closeModal = useCallback(() => {
    console.log('🔄 useCompetitionSetupTutorial: Closing modal')
    setIsModalOpen(false)
    // Also reset debug mode when closing
    setIsDebugMode(false)
  }, [])

  /**
   * Set up keyboard listener for debug mode (Ctrl+T)
   */
  useEffect(() => {
    if (!isDebugUser) {
      return
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 't') {
        event.preventDefault()
        console.log('🐛 useCompetitionSetupTutorial: Debug mode activated')
        setIsDebugMode(true)
        setIsModalOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [isDebugUser])

  /**
   * Fetch tutorial status on mount if conditions are met
   */
  useEffect(() => {
    if (shouldRun) {
      fetchTutorialStatus()
    }
  }, [shouldRun, fetchTutorialStatus])

  /**
   * Determine if modal should be shown automatically
   * Only show if user is coming from onboarding wizard or in debug mode
   */
  useEffect(() => {
    if (!isLoading && shouldRun && !isDebugMode) {
      // Only auto-show if coming from onboarding wizard and tutorial not started
      if (isFromOnboarding && tutorialStatus === 'not_started') {
        console.log(
          '🔄 useCompetitionSetupTutorial: Auto-opening modal for competition setup from onboarding'
        )
        setIsModalOpen(true)
        // Mark as in_progress when modal opens for first time
        updateTutorialStatus('in_progress')
      }
    }
  }, [
    isLoading,
    shouldRun,
    tutorialStatus,
    isDebugMode,
    isFromOnboarding,
    updateTutorialStatus,
  ])

  // Determine what should be shown
  const shouldShowModal = isModalOpen

  return {
    shouldShowModal,
    tutorialStatus,
    isLoading,
    error,
    updateTutorialStatus,
    openModal,
    closeModal,
    isModalOpen,
  }
}
